import React, { useState, useEffect } from 'react';
import { X, Plus, FileText, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import documentService from '../../services/documentService';

/**
 * ChooseDocModal - Let user pick an existing doc or create new one
 */
export default function ChooseDocModal({ isOpen, onClose, onSelectDoc, userId, onCreateNew }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateView, setShowCreateView] = useState(false);
  const [newDocTitle, setNewDocTitle] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const loadDocs = async () => {
      if (!isOpen || !userId) return;

      setLoading(true);
      try {
        const { data } = await documentService.getRecentDocs(userId, { limit: 10 });
        setDocs(data || []);
      } catch (error) {
        console.error('Error loading docs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDocs();
  }, [isOpen, userId]);

  const handleCreateNew = async () => {
    if (creating) return;

    setCreating(true);
    try {
      await onCreateNew(newDocTitle.trim() || 'Untitled');
      setNewDocTitle('');
      setShowCreateView(false);
    } catch (error) {
      console.error('Error creating doc:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setShowCreateView(false);
    setNewDocTitle('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[80vh] flex flex-col pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  {showCreateView && (
                    <button
                      onClick={() => setShowCreateView(false)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <ArrowLeft size={18} className="text-gray-600" />
                    </button>
                  )}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {showCreateView ? 'Create new doc' : 'Add to a doc'}
                    </h3>
                    {!showCreateView && (
                      <p className="text-sm text-gray-500 mt-0.5">Pick where this should go</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X size={20} className="text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {showCreateView ? (
                  /* Create New Doc View */
                  <div className="p-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Doc title (optional)
                    </label>
                    <input
                      type="text"
                      value={newDocTitle}
                      onChange={(e) => setNewDocTitle(e.target.value)}
                      placeholder="Untitled"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleCreateNew();
                        }
                      }}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                ) : (
                  /* Doc List View */
                  <div className="p-4">
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                      </div>
                    ) : docs.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText size={48} className="mx-auto text-gray-300 mb-3" />
                        <p className="text-sm text-gray-500">No docs yet</p>
                        <p className="text-xs text-gray-400 mt-1">Create your first doc below</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {docs.map((doc) => (
                          <button
                            key={doc.id}
                            onClick={() => onSelectDoc(doc.id)}
                            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors text-left group"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText size={14} className="text-gray-400 flex-shrink-0" />
                                <p className="text-sm font-medium text-gray-900 truncate">{doc.title || 'Untitled'}</p>
                              </div>
                              <p className="text-xs text-gray-500">
                                {formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })}
                              </p>
                            </div>
                            <div className="flex-shrink-0 ml-3 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-100 group-hover:bg-purple-200 rounded transition-colors">
                              Add here
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
                {showCreateView ? (
                  <>
                    <button
                      onClick={() => setShowCreateView(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateNew}
                      disabled={creating}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {creating ? (
                        <>Creating...</>
                      ) : (
                        <>
                          <Plus size={16} />
                          Create and add
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleClose}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => setShowCreateView(true)}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-100 hover:bg-purple-200 rounded-lg transition-colors"
                    >
                      <Plus size={16} />
                      Create new doc
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}



